import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { supabase } from "../../utils/supabaseClient";

const FedaPayRemboursementButton = ({ loan, onSuccess, onError, customAmount }) => {
  const buttonRef = useRef(null);
  const { user } = useAuth();
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);

  useEffect(() => {
    const existing = document.getElementById("fedapay-checkout");
    if (existing) {
      console.log("[FedaPay] Script déjà chargé");
      setScriptLoaded(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://cdn.fedapay.com/checkout.js?v=1.1.7";
    script.id = "fedapay-checkout";
    script.onload = () => {
      console.log("[FedaPay] Script chargé avec succès");
      setScriptLoaded(true);
    };
    script.onerror = () => {
      console.error("[FedaPay] ❌ Erreur chargement script depuis https://cdn.fedapay.com/checkout.js");
    };
    document.body.appendChild(script);
  }, []);

  const cleanupFedaPay = () => {
    try {
      if (window.FedaPay) window.FedaPay.destroy();
    } catch (e) {}
    setIsInitialized(false);
  };

  const validateAndFormatPhone = (raw) => {
    const fallback = "97223174";
    if (!raw) return fallback;
    const clean = raw.toString().replace(/[\s\-]/g, "");
    if (clean.startsWith("+229") || clean.startsWith("229")) return clean.slice(-8);
    if (clean.startsWith("0")) return clean.slice(1);
    return clean;
  };

  useEffect(() => {
    // Don't initialize FedaPay here. Use handleCustomPayment for all cases.
    // This prevents creating unwanted transactions with wrong amounts on FedaPay API.
  }, []);

  const handlePayment = async (paymentAmount) => {
    if (!paymentAmount || paymentAmount <= 0) {
      onError?.({ message: 'Montant invalide' });
      return;
    }

    setPaymentProcessing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      console.log('[PAYMENT] Validation:', { amount: paymentAmount, customAmount });

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'}/api/loan-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          loanId: loan.id,
          amount: paymentAmount,
          userId: user.id
        })
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        onError?.({ message: result.error || 'Montant invalide' });
        setPaymentProcessing(false);
        return;
      }

      console.log('[PAYMENT] Transaction created:', { id: result.transactionId, amount: result.amount });

      if (!window.FedaPay) {
        onError?.({ message: 'FedaPay not available' });
        setPaymentProcessing(false);
        return;
      }

      try {
        window.FedaPay.destroy();
      } catch (e) {}

      console.log('[PAYMENT] Initializing FedaPay modal with:', { id: result.transactionId, amount: result.amount });

      try {
        // Initialize returns widget object - must call .open() to show modal
        const widget = window.FedaPay.init({
          public_key: process.env.REACT_APP_FEDAPAY_PUBLIC_KEY,
          transaction: {
            id: result.transactionId,
            amount: result.amount
          },
          modal: true,

          onComplete: ({ reason, transaction }) => {
            console.log('[PAYMENT] onComplete:', reason);
            if (reason === window.FedaPay.CHECKOUT_COMPLETED) {
              setPaymentProcessing(false);
              onSuccess?.({ transaction: { reference: transaction?.reference || transaction?.id, id: transaction?.id } });
            } else {
              setPaymentProcessing(false);
            }
          },

          onError: (error) => {
            console.error('[PAYMENT] onError:', error);
            setPaymentProcessing(false);
            const msg = typeof error === 'string' ? error : error?.message || 'Payment error';
            onError?.({ message: msg });
          },

          onClose: () => {
            console.log('[PAYMENT] onClose');
            setPaymentProcessing(false);
          },
        });

        console.log('[PAYMENT] FedaPay initialized, opening modal...');
        widget.open();
        console.log('[PAYMENT] Modal open called');
      } catch (initError) {
        console.error('[PAYMENT] FedaPay error:', initError);
        setPaymentProcessing(false);
        onError?.({ message: 'FedaPay failed: ' + initError.message });
      }
    } catch (error) {
      console.error('[PAYMENT] Error:', error);
      onError?.({ message: error.message || 'Payment failed' });
      setPaymentProcessing(false);
    }
  };

  const handleCustomPayment = () => {
    const amount = customAmount || parseInt(loan?.remainingAmount, 10);
    handlePayment(amount);
  };

  return (
    <button
      ref={buttonRef}
      onClick={customAmount ? handleCustomPayment : undefined}
      disabled={!scriptLoaded || !loan || (customAmount && customAmount <= 0) || paymentProcessing}
      className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-4 rounded-xl font-semibold shadow-lg flex items-center justify-center disabled:opacity-60"
    >
      <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
      </svg>
      {paymentProcessing ? 'Traitement...' : 'Rembourser mon prêt'}
    </button>
  );
};

export default FedaPayRemboursementButton;
